import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AssistantAction } from '../../../core/services/voice-actions/models';
import { readActionPayloadValue } from '../../../core/services/voice-actions/voice-action-payload';
import { GlobalNavigationVoiceFlowService } from './global-navigation-voice-flow.service';

type QueryValue = string | number | boolean;
type QueryBag = Record<string, QueryValue>;
type PathTarget = {
  commands: string[];
  queryParams: QueryBag;
};

@Injectable({
  providedIn: 'root'
})
export class NavigationVoiceActionService {
  private readonly router = inject(Router);
  private readonly globalNavigationFlow = inject(GlobalNavigationVoiceFlowService);

  async open(action: AssistantAction): Promise<void> {
    const explicitRoute = this.normalizeRoute(this.readPayloadValue<string>(action, 'route'));
    const routeId = this.readPayloadValue<string>(action, 'routeId');
    const explicitQueryParams = this.normalizeQueryParams(this.readPayloadValue<unknown>(action, 'queryParams'));

    let target: PathTarget | undefined;

    if (explicitRoute) {
      target = this.resolveFromRoute(explicitRoute);
      target = this.mergeQueryParams(target, explicitQueryParams);
    } else if (routeId) {
      const flowItem = this.globalNavigationFlow.getAllowedItem(routeId);
      target = this.resolveFromFlowItem(flowItem);
      target = target ? this.mergeQueryParams(target, explicitQueryParams) : undefined;
    }

    if (!target || !target.commands.length) {
      throw new Error('No valid navigation target was provided.');
    }

    if (target) {
      const hasQuery = Object.keys(target.queryParams).length > 0;
      await this.router.navigate(target.commands, hasQuery ? { queryParams: target.queryParams } : {});
    }
  }

  private resolveFromRoute(route: string): PathTarget {
    const trimmed = route.trim();
    const [rawPath, rawQuery] = trimmed.split('?');
    const commands = rawPath
      .split('/')
      .filter(Boolean)
      .map((segment) => decodeURIComponent(segment));

    if (commands.length) {
      commands[0] = `/${commands[0]}`;
    }

    const queryParams = this.normalizeQueryParamsFromString(rawQuery);

    return {
      commands,
      queryParams
    };
  }

  private resolveFromFlowItem(item: ReturnType<GlobalNavigationVoiceFlowService['getAllowedItem']> | undefined): PathTarget | undefined {
    if (!item?.metadata?.route) {
      return undefined;
    }

    const base = this.resolveFromRoute(item.metadata.route);
    const combinedQuery = {
      ...base.queryParams,
      ...this.normalizeQueryParams(item.metadata.queryParams),
      ...(item.queryParams ? this.normalizeQueryParams(item.queryParams) : {})
    };

    return {
      ...base,
      queryParams: combinedQuery
    };
  }

  private mergeQueryParams(target: PathTarget, queryParams: QueryBag): PathTarget {
    if (!queryParams || !Object.keys(queryParams).length) {
      return target;
    }

    return {
      commands: target.commands,
      queryParams: {
        ...target.queryParams,
        ...queryParams
      }
    };
  }

  private normalizeRoute(value: unknown): string | undefined {
    if (typeof value !== 'string') return undefined;
    const trimmed = value.trim();
    return trimmed ? trimmed : undefined;
  }

  private normalizeQueryParams(value: unknown): QueryBag {
    if (!value) return {};

    if (typeof value === 'string') {
      return this.normalizeQueryParamsFromString(value);
    }

    if (typeof value !== 'object' || Array.isArray(value)) return {};

    const entries = Object.entries(value as Record<string, unknown>);
    if (!entries.length) return {};

    const output: QueryBag = {};
    for (const [key, rawValue] of entries) {
      if (!key || rawValue === undefined || rawValue === null) continue;
      const normalized = this.normalizePrimitive(rawValue);
      if (normalized === undefined) continue;
      output[key] = normalized;
    }

    return output;
  }

  private normalizeQueryParamsFromString(queryString: string | undefined): QueryBag {
    const query = (queryString || '').trim();
    if (!query) return {};

    const search = query.startsWith('?') ? query.slice(1) : query;
    const parsed = new URLSearchParams(search);
    const output: QueryBag = {};

    for (const [key, rawValue] of parsed.entries()) {
      const normalized = this.normalizePrimitive(rawValue);
      if (normalized === undefined) continue;
      output[key] = normalized;
    }

    return output;
  }

  private normalizePrimitive(value: unknown): QueryValue | undefined {
    if (typeof value === 'string') {
      const lowered = value.toLowerCase();
      if (lowered === 'true') return true;
      if (lowered === 'false') return false;
      if (!Number.isNaN(Number(value)) && value.trim() !== '') return Number(value);
      return value;
    }
    if (typeof value === 'number' || typeof value === 'boolean') return value;
    return undefined;
  }

  private readPayloadValue<T>(action: AssistantAction, key: string): T | undefined {
    return readActionPayloadValue<T>(action, key);
  }
}
